<template>
    <div class="rich-text-editor-container">
        <!-- Toolbar de Quill (integrado) -->
        <div ref="editor" class="quill-editor"></div>

        <!-- Contador de caracteres SIN LÍMITE -->
        <div class="char-counter">
            Caracteres: {{ charCount }}
            <span v-if="charCount > 10000" class="info">
                (Se dividirá automáticamente en la base de datos)
            </span>
        </div>
    </div>
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

// Registrar módulo personalizado para manejar estilos CSS
const InlineStyle = Quill.import('blots/inline');

class StyleBlot extends InlineStyle {
    static create(value) {
        const node = super.create(value);
        if (value && typeof value === 'object') {
            Object.keys(value).forEach(style => {
                node.style.setProperty(style, value[style]);
            });
        }
        return node;
    }

    static formats(node) {
        const styles = {};
        for (let i = 0; i < node.style.length; i++) {
            const property = node.style[i];
            styles[property] = node.style.getPropertyValue(property);
        }
        return styles;
    }
}
StyleBlot.blotName = 'style';
StyleBlot.tagName = 'span';

Quill.register(StyleBlot);

// Extender el módulo de tamaño de fuente para usar CSS
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = [
    '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'
];
Quill.register(SizeStyle, true);

export default {
    name: 'RichTextEditor',
    props: {
        value: {
            type: String,
            default: ''
        },
        height: {
            type: Number,
            default: 400
        },
        placeholder: {
            type: String,
            default: 'Escribe el contenido aquí...'
        },
        // Nueva prop para forzar estilos CSS
        useInlineStyles: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            quill: null,
            content: '',
            charCount: 0,
            defaultFontSize: '14px' // Tamaño por defecto
        };
    },
    watch: {
        value(newVal) {
            if (this.quill && newVal !== this.content) {
                this.setContent(newVal);
            }
        }
    },
    mounted() {
        this.initQuill();
        this.setupCSSStyles();
    },
    beforeDestroy() {
        if (this.quill) {
            this.quill.off('text-change');
        }
    },
    methods: {
        initQuill() {
            // Configuración del toolbar similar a Word
            const toolbarOptions = [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'font': [] }],
                [{ 'size': SizeStyle.whitelist }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'align': [] }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                ['blockquote', 'code-block'],
                ['link', 'image'],
                ['clean']
            ];

            this.quill = new Quill(this.$refs.editor, {
                theme: 'snow',
                placeholder: this.placeholder,
                modules: {
                    toolbar: toolbarOptions
                    
                }
            });

            // Establecer altura
            this.quill.container.style.height = `${this.height}px`;

            // Habilitar estilos CSS
            if (this.useInlineStyles) {
                this.enableInlineStyles();
            }

            // Cargar contenido inicial
            if (this.value) {
                this.setContent(this.value);
            } else {
                // Aplicar estilo por defecto al contenido inicial
                this.applyDefaultStyles();
            }

            // Escuchar cambios
            this.quill.on('text-change', () => {
                this.updateContent();
            });

            // También contar cambios de formato
            this.quill.on('editor-change', () => {
                this.updateCharCount();
            });

            // Interceptar operaciones de formato para agregar CSS
            this.interceptFormatOperations();
        },

        setupCSSStyles() {
            // Agregar estilos CSS para asegurar que se guarden
            const style = document.createElement('style');
            style.textContent = `
                .quill-editor .ql-editor * {
                    font-family: inherit;
                }
                .quill-editor .ql-editor strong, 
                .quill-editor .ql-editor b {
                    font-weight: bold !important;
                }
                .quill-editor .ql-editor em,
                .quill-editor .ql-editor i {
                    font-style: italic !important;
                }
                .quill-editor .ql-editor u {
                    text-decoration: underline !important;
                }
                .quill-editor .ql-editor s,
                .quill-editor .ql-editor strike {
                    text-decoration: line-through !important;
                }
            `;
            document.head.appendChild(style);
        },

        enableInlineStyles() {
            // Configurar Quill para usar CSS en lugar de atributos HTML simples
            const originalFormat = this.quill.format;
            
            this.quill.format = (name, value) => {
                if (name === 'bold' && value) {
                    // Usar estilo CSS en lugar de <strong>
                    const range = this.quill.getSelection();
                    if (range) {
                        this.quill.formatText(range.index, range.length, {
                            'font-weight': 'bold'
                        });
                    }
                    return;
                }
                
                if (name === 'italic' && value) {
                    // Usar estilo CSS en lugar de <em>
                    const range = this.quill.getSelection();
                    if (range) {
                        this.quill.formatText(range.index, range.length, {
                            'font-style': 'italic'
                        });
                    }
                    return;
                }
                
                if (name === 'underline' && value) {
                    // Usar estilo CSS
                    const range = this.quill.getSelection();
                    if (range) {
                        this.quill.formatText(range.index, range.length, {
                            'text-decoration': 'underline'
                        });
                    }
                    return;
                }
                
                // Para otros formatos, usar el método original
                originalFormat.call(this.quill, name, value);
            };
        },

        interceptFormatOperations() {
            // Interceptar operaciones del toolbar
            const toolbar = this.quill.getModule('toolbar');

            // Sobrescribir manejadores para usar CSS
            toolbar.handlers.bold = () => {
                const range = this.quill.getSelection();
                if (range) {
                    const format = this.quill.getFormat(range);
                    const isBold = format.bold || format['font-weight'] === 'bold';
                    
                    if (isBold) {
                        // Quitar negrita
                        this.quill.formatText(range.index, range.length, {
                            'bold': false,
                            'font-weight': 'normal'
                        });
                    } else {
                        // Aplicar negrita con CSS
                        this.quill.formatText(range.index, range.length, {
                            'bold': true,
                            'font-weight': 'bold'
                        });
                    }
                }
            };

            toolbar.handlers.italic = () => {
                const range = this.quill.getSelection();
                if (range) {
                    const format = this.quill.getFormat(range);
                    const isItalic = format.italic || format['font-style'] === 'italic';
                    
                    if (isItalic) {
                        // Quitar cursiva
                        this.quill.formatText(range.index, range.length, {
                            'italic': false,
                            'font-style': 'normal'
                        });
                    } else {
                        // Aplicar cursiva con CSS
                        this.quill.formatText(range.index, range.length, {
                            'italic': true,
                            'font-style': 'italic'
                        });
                    }
                }
            };

            toolbar.handlers.underline = () => {
                const range = this.quill.getSelection();
                if (range) {
                    const format = this.quill.getFormat(range);
                    const isUnderline = format.underline || 
                        (format['text-decoration'] && format['text-decoration'].includes('underline'));
                    
                    if (isUnderline) {
                        // Quitar subrayado
                        this.quill.formatText(range.index, range.length, {
                            'underline': false,
                            'text-decoration': 'none'
                        });
                    } else {
                        // Aplicar subrayado con CSS
                        this.quill.formatText(range.index, range.length, {
                            'underline': true,
                            'text-decoration': 'underline'
                        });
                    }
                }
            };
        },

        setContent(content) {
            if (!content) {
                this.quill.setText('');
                this.content = '';
                this.charCount = 0;
                this.applyDefaultStyles();
                return;
            }

            // Verificar si es HTML o texto plano
            if (this.isHTML(content)) {
                // Limpiar y establecer contenido
                this.quill.root.innerHTML = content;
                
                // Asegurar que todo el contenido tenga estilos CSS
                if (this.useInlineStyles) {
                    this.ensureInlineStyles();
                }
            } else {
                // Si es texto plano, convertirlo a párrafos con estilos
                const paragraphs = content.split('\n').filter(p => p.trim());
                this.quill.setText(''); // Limpiar primero
                
                if (paragraphs.length > 0) {
                    paragraphs.forEach((para, index) => {
                        if (index > 0) {
                            this.quill.insertText(this.quill.getLength(), '\n', {
                                'font-size': this.defaultFontSize
                            });
                        }
                        
                        // Insertar texto con estilo por defecto
                        const lengthBefore = this.quill.getLength();
                        this.quill.insertText(this.quill.getLength(), para, {
                            'font-size': this.defaultFontSize
                        });
                        
                        // Formatear el texto recién insertado
                        this.quill.formatText(lengthBefore, para.length, {
                            'font-size': this.defaultFontSize
                        });
                    });
                } else {
                    this.quill.setText(content);
                    this.applyDefaultStyles();
                }
            }

            this.updateContent();
        },

        ensureInlineStyles() {
            // Recorrer todo el contenido y asegurar que tenga estilos CSS
            const editor = this.quill.root;
            const elements = editor.querySelectorAll('*');
            
            elements.forEach(element => {
                // Si es un elemento de texto con formato pero sin estilo CSS
                if (element.tagName === 'STRONG' || element.tagName === 'B') {
                    element.style.fontWeight = 'bold';
                }
                
                if (element.tagName === 'EM' || element.tagName === 'I') {
                    element.style.fontStyle = 'italic';
                }
                
                if (element.tagName === 'U') {
                    element.style.textDecoration = 'underline';
                }
                
                if (element.tagName === 'S' || element.tagName === 'STRIKE') {
                    element.style.textDecoration = 'line-through';
                }
                
                // Asegurar que los párrafos tengan tamaño de fuente
                if (element.tagName === 'P' && !element.style.fontSize) {
                    element.style.fontSize = this.defaultFontSize;
                }
                
                // Convertir headers a estilos CSS
                if (element.tagName.match(/^H[1-6]$/)) {
                    const level = parseInt(element.tagName.charAt(1));
                    const sizes = {
                        1: '32px', 2: '28px', 3: '24px', 
                        4: '20px', 5: '18px', 6: '16px'
                    };
                    element.style.fontSize = sizes[level] || this.defaultFontSize;
                    element.style.fontWeight = 'bold';
                }
            });
        },

        applyDefaultStyles() {
            // Aplicar estilo por defecto a todo el contenido
            const length = this.quill.getLength();
            if (length > 0) {
                this.quill.formatText(0, length, {
                    'font-size': this.defaultFontSize
                });
            }
        },

        isHTML(str) {
            if (typeof str !== 'string') return false;
            const htmlRegex = /<[a-z][\s\S]*>/i;
            return htmlRegex.test(str);
        },

        updateContent() {
            const html = this.getHTMLWithStyles();
            const text = this.quill.getText();

            // Solo actualizar si cambió
            if (html !== this.content) {
                this.content = html;
                this.$emit('input', html);
                this.$emit('change', {
                    html: html,
                    text: text,
                    delta: this.quill.getContents()
                });
            }

            this.updateCharCount();
        },

        getHTMLWithStyles() {
            if (!this.quill) return '';
            
            // Obtener el HTML del editor
            let html = this.quill.root.innerHTML;
            
            // Post-procesar para asegurar estilos CSS
            if (this.useInlineStyles) {
                html = this.addInlineStyles(html);
            }
            
            return html;
        },

        addInlineStyles(html) {
            // Usar DOMParser para manipular el HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Función recursiva para agregar estilos
            const processElement = (element) => {
                // Procesar hijos primero
                Array.from(element.children).forEach(processElement);
                
                // Agregar estilos según la etiqueta
                if (element.tagName === 'STRONG' || element.tagName === 'B') {
                    element.style.fontWeight = 'bold';
                    // Opcional: convertir a span para mantener solo CSS
                    if (this.useInlineStyles) {
                        const span = document.createElement('span');
                        span.innerHTML = element.innerHTML;
                        span.style.fontWeight = 'bold';
                        element.parentNode.replaceChild(span, element);
                    }
                }
                
                if (element.tagName === 'EM' || element.tagName === 'I') {
                    element.style.fontStyle = 'italic';
                }
                
                if (element.tagName === 'U') {
                    element.style.textDecoration = 'underline';
                }
                
                // Tamaños de fuente
                const fontSize = element.style.fontSize || 
                                element.getAttribute('data-font-size') ||
                                (element.tagName.match(/^H[1-6]$/) ? 
                                    this.getHeaderSize(element.tagName) : null);
                
                if (fontSize && !element.style.fontSize) {
                    element.style.fontSize = fontSize;
                }
                
                // Si no tiene tamaño, asignar por defecto
                if (!element.style.fontSize && 
                    ['P', 'DIV', 'SPAN'].includes(element.tagName) &&
                    element.children.length === 0) {
                    element.style.fontSize = this.defaultFontSize;
                }
            };
            
            // Procesar todo el documento
            Array.from(doc.body.children).forEach(processElement);
            
            return doc.body.innerHTML;
        },

        getHeaderSize(tagName) {
            const sizes = {
                'H1': '32px',
                'H2': '28px', 
                'H3': '24px',
                'H4': '20px',
                'H5': '18px',
                'H6': '16px'
            };
            return sizes[tagName] || this.defaultFontSize;
        },

        updateCharCount() {
            // Contar caracteres del texto plano (sin HTML)
            const text = this.quill.getText();
            this.charCount = text.length;

            // EMITIR siempre el conteo (sin límites)
            this.$emit('char-count', this.charCount);
        },

        // Métodos públicos
        getHTML() {
            return this.getHTMLWithStyles();
        },

        getText() {
            return this.quill.getText();
        },

        getDelta() {
            return this.quill.getContents();
        },

        clear() {
            this.quill.setText('');
            this.updateContent();
        },

        // Métodos para formateo programático con CSS
        formatBold() {
            const range = this.quill.getSelection();
            if (range) {
                this.quill.formatText(range.index, range.length, {
                    'font-weight': 'bold'
                });
            }
        },

        formatItalic() {
            const range = this.quill.getSelection();
            if (range) {
                this.quill.formatText(range.index, range.length, {
                    'font-style': 'italic'
                });
            }
        },

        formatUnderline() {
            const range = this.quill.getSelection();
            if (range) {
                this.quill.formatText(range.index, range.length, {
                    'text-decoration': 'underline'
                });
            }
        },

        setFontSize(size) {
            const range = this.quill.getSelection();
            if (range) {
                // Asegurar que el tamaño tenga 'px'
                const fontSize = typeof size === 'number' ? `${size}px` : size;
                this.quill.formatText(range.index, range.length, {
                    'font-size': fontSize
                });
            }
        },

        setHeader(level) {
            const range = this.quill.getSelection();
            if (range) {
                // Primero quitar cualquier formato de header existente
                this.quill.formatText(range.index, range.length, 'header', false);
                
                // Aplicar el nuevo header
                this.quill.formatText(range.index, range.length, 'header', level);
                
                // También aplicar estilo CSS
                const sizes = {
                    1: '32px', 2: '28px', 3: '24px', 
                    4: '20px', 5: '18px', 6: '16px'
                };
                if (sizes[level]) {
                    this.quill.formatText(range.index, range.length, {
                        'font-size': sizes[level],
                        'font-weight': 'bold'
                    });
                }
            }
        },

        // Método para obtener contenido con estilos explícitos
        getStyledContent() {
            return {
                html: this.getHTMLWithStyles(),
                text: this.getText(),
                delta: this.getDelta()
            };
        }
    }
};
</script>

<style scoped>
.rich-text-editor-container {
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
}

.quill-editor {
    background: white;
}

.char-counter {
    padding: 5px 10px;
    background: #f5f5f5;
    border-top: 1px solid #ddd;
    font-size: 12px;
    text-align: right;
    color: #666;
}

.char-counter.near-limit {
    background: #fff3cd;
    color: #856404;
}

.char-counter .warning {
    color: #ff9800;
    font-weight: bold;
}

.char-counter .error {
    color: #f44336;
    font-weight: bold;
}

/* Personalizar toolbar de Quill */
:deep(.ql-toolbar.ql-snow) {
    border: none;
    border-bottom: 1px solid #ddd;
    padding: 8px;
}

:deep(.ql-container.ql-snow) {
    border: none;
    font-size: 14px;
}

:deep(.ql-editor) {
    min-height: 300px;
    padding: 15px;
    line-height: 1.6;
}

:deep(.ql-editor.ql-blank::before) {
    font-style: normal;
    color: #999;
}

:deep(.ql-toolbar button:hover),
:deep(.ql-toolbar button:focus),
:deep(.ql-toolbar button.ql-active) {
    color: #1976d2;
}

:deep(.ql-toolbar .ql-picker-item:hover) {
    color: #1976d2;
}

/* Estilos para tamaños de fuente en el toolbar */
:deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value])::before,
:deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value])::before {
    content: attr(data-value) !important;
}

:deep(.ql-snow .ql-picker.ql-size .ql-picker-label)::before,
:deep(.ql-snow .ql-picker.ql-size .ql-picker-item)::before {
    content: '14px' !important; /* Valor por defecto */
}
</style>